import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { BackButton } from "@/components/back-button";
import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { releases } from "@/data/releases";

export const Route = createFileRoute("/sound")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/sound" },
      { title: "Sound — Records | Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Records by Luiza Jesus do Prado and Hifa Cybe — albums, EPs and collaborations, playable directly on the site.",
      },
      { property: "og:title", content: "Sound — Records | Luiza Jesus do Prado" },
      { property: "og:description", content: "Albums, EPs and collaborations, playable here." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Sound,
});

const icons = {
  spotify: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M7.5 9.5c3-1 6.5-.7 9 .8M8 12.5c2.5-.7 5.2-.4 7.3.8M8.5 15.3c2-.5 4-.3 5.7.6" />
    </>
  ),
  apple: <path d="M9 18V6l10-2v12M9 18a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM19 16a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />,
  bandcamp: <path d="M3 17L9 7h12l-6 10z" />,
  deezer: <path d="M4 18h3M9 18h3M14 18h3M19 18h1M9 14h3M14 14h3M19 14h1M14 10h3M19 10h1M19 6h1" />,
};

const platforms: { label: string; href: string; icon: keyof typeof icons }[] = [
  { label: "Spotify — Hifa Cybe", href: "https://open.spotify.com/intl-pt/artist/0NzpT3pNycVS6uoFKGUk7R", icon: "spotify" },
  { label: "Spotify — Luiza Prado", href: "https://open.spotify.com/intl-pt/artist/3pNVBkJAivjuYrz8gcA4S9", icon: "spotify" },
  { label: "Spotify — Homens com Sabor Atrás", href: "https://open.spotify.com/intl-pt/artist/1Aoiybn0UHC32iqJ44jezE", icon: "spotify" },
  { label: "Apple Music — Luiza Prado", href: "https://music.apple.com/es/artist/luiza-prado/1536796715", icon: "apple" },
  { label: "Apple Music — Hifa Cybe", href: "https://music.apple.com/fr/artist/hifa-cybe/1546631802", icon: "apple" },
  { label: "Bandcamp — Correndo Contra o Tempo", href: "https://flaviagoa.bandcamp.com/track/flavia-goa-hifa-cybe-correndo-contra-o-tempo", icon: "bandcamp" },
  { label: "Deezer", href: "https://www.deezer.com/br/artist/10584853", icon: "deezer" },
];

function Sound() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />
      <BackButton />

      {/* HERO */}
      <section className="relative h-[62svh] w-full overflow-hidden bg-ink">
        <div className="relative flex h-full flex-col justify-between px-6 py-28 md:px-16 md:py-32">
          <div className="flex items-start justify-between gap-8 font-mono text-[10px] uppercase leading-[1.7] tracking-[0.18em] text-bone/70">
            <span>
              Records
              <br />/ listen here
            </span>
            <span className="text-right">
              {String(releases.length).padStart(2, "0")} releases
            </span>
          </div>

          <div>
            <h1 className="font-display text-[clamp(3rem,16vw,11rem)] font-light uppercase leading-[0.85] tracking-[0.02em]">
              Sound
            </h1>
            <span className="mt-6 block h-px w-40 bg-bone/40" />
          </div>
        </div>
      </section>

      <main id="main-content" className="px-6 pb-40 md:px-16">
        <ul className="border-t border-bone/12">
          {releases.map((r, i) => {
            const on = open === i;
            return (
              <li key={`${r.title}-${i}`} className="border-b border-bone/12">
                <button
                  type="button"
                  onClick={() => setOpen(on ? null : i)}
                  className="group grid w-full grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-6 py-6 text-left md:grid-cols-[88px_minmax(0,1fr)_auto] md:py-8"
                >
                  <img
                    src={r.cover}
                    alt={r.title}
                    loading="lazy"
                    className={`aspect-square w-full object-cover transition-all duration-700 ${
                      on ? "" : "grayscale group-hover:grayscale-0"
                    }`}
                  />
                  <span className="min-w-0">
                    <span
                      className={`block font-display text-[clamp(1.3rem,4.5vw,2.8rem)] font-light uppercase leading-[0.98] tracking-[0.02em] transition-opacity duration-500 ${
                        on ? "text-bone" : "text-bone/70 group-hover:text-bone/85"
                      }`}
                    >
                      {r.title}
                    </span>
                    <span className="mt-2 block font-mono text-[9px] uppercase tracking-[0.22em] text-bone/65">
                      {r.artist}
                    </span>
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-bone/65">
                    {r.embed ? (on ? "Close" : "Play") : "Bandcamp ↗"}
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-[max-height] duration-700 ${
                    on ? "max-h-[420px]" : "max-h-0"
                  }`}
                >
                  {on &&
                    (r.embed ? (
                      <iframe
                        title={r.title}
                        src={r.embed}
                        loading="lazy"
                        allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        className={`mb-8 w-full border-0 ${r.platform === "Bandcamp" ? "h-[120px]" : "h-[352px]"}`}
                      />
                    ) : (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mb-8 block font-mono text-[10px] uppercase tracking-[0.26em] text-bone/60 hover:text-bone"
                      >
                        Listen on {r.platform} ↗
                      </a>
                    ))}
                </div>
              </li>
            );
          })}
        </ul>

        {/* PLATFORMS */}
        <section className="mt-28">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-bone/65">
            Also on
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-6 border-t border-bone/12 pt-6">
            {platforms.map((p) => (
              <a
                key={p.href}
                href={p.href}
                target="_blank"
                rel="noreferrer"
                aria-label={p.label}
                title={p.label}
                className="text-bone/70 transition-colors hover:text-bone"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  {icons[p.icon]}
                </svg>
              </a>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
